import { supabase } from "@/lib/supabase";

export interface SafetyRuleRow {
  id?: string;
  code: string;
  name: string;
  description: string | null;
  category: string;
  severity: string;
  conditions?: Record<string, unknown>;
  config?: Record<string, unknown>;
  is_active?: boolean;
  order_index?: number;
  created_at?: string;
  updated_at?: string;
}

function normalizeCategory(category: string | null | undefined): string {
  const raw = (category || "").trim().toLowerCase();
  const compact = raw.replace(/\s+/g, "_");
  const map: Record<string, string> = {
    scheduling: "scheduling",
    ordering: "ordering",
    dispatch: "dispatch",
    job_creation: "job_creation",
    "job creation": "job_creation",
    sales: "sales",
    pricing: "pricing",
    compliance: "compliance",
    insights: "insights",
  };
  return map[compact] || map[raw] || "compliance";
}

function toLegacyCategory(category: string): string {
  return category
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function buildCategoryCandidates(category: string | null | undefined): string[] {
  const normalized = normalizeCategory(category);
  const raw = (category || "").trim();
  const legacy = toLegacyCategory(normalized);
  const fallbackNormalized = "scheduling";
  const fallbackLegacy = "Scheduling";

  return Array.from(
    new Set(
      [normalized, legacy, raw, fallbackNormalized, fallbackLegacy]
        .map((v) => v.trim())
        .filter(Boolean),
    ),
  );
}

function normalizeSeverity(severity: string | null | undefined): string {
  const raw = (severity || "").trim().toLowerCase();
  if (raw === "block" || raw === "hard_block" || raw === "hard block") return "hard_block";
  if (raw === "warning" || raw === "soft_block" || raw === "soft block") return "soft_block";
  if (raw === "info" || raw === "informational") return "informational";
  return "informational";
}

function severityToDb(severity: string | null | undefined): "Block" | "Warning" | "Info" {
  const raw = (severity || "").trim().toLowerCase();
  if (raw === "block" || raw === "hard_block") return "Block";
  if (raw === "warning" || raw === "soft_block") return "Warning";
  return "Info";
}

export const SafetyRulesService = {
  async getAll() {
    const { data, error } = await supabase
      .from("safety_rules")
      .select("*")
      .order("order_index", { ascending: true });
    return { data, error };
  },

  async getActive() {
    const { data, error } = await supabase
      .from("safety_rules")
      .select("*")
      .eq("is_active", true)
      .order("order_index", { ascending: true });
    return { data, error };
  },

  async getByCode(code: string) {
    const { data, error } = await supabase
      .from("safety_rules")
      .select("*")
      .eq("code", code)
      .single();
    return { data, error };
  },

  async create(rule: Omit<SafetyRuleRow, "id" | "created_at" | "updated_at">) {
    const basePayload = {
      ...rule,
      severity: severityToDb(rule.severity),
    };
    const categoryCandidates = buildCategoryCandidates(rule.category);
    let lastError: unknown = null;

    for (const category of categoryCandidates) {
      const payload = { ...basePayload, category };
      const { data, error } = await supabase
        .from("safety_rules")
        .insert([payload])
        .select()
        .single();
      if (!error) return { data, error: null };
      lastError = error;

      const code = (error as { code?: string } | null)?.code;
      if (code !== "23514") {
        return { data: null, error };
      }
    }

    return { data: null, error: lastError };
  },

  async update(code: string, updates: Partial<SafetyRuleRow>) {
    const payload: Partial<SafetyRuleRow> = { ...updates };
    if (typeof updates.severity === "string") {
      payload.severity = severityToDb(updates.severity);
    }

    const categoryCandidates = typeof updates.category === "string"
      ? buildCategoryCandidates(updates.category)
      : [undefined];

    let lastError: unknown = null;
    for (const category of categoryCandidates) {
      const patch: Partial<SafetyRuleRow> =
        typeof category === "string" ? { ...payload, category } : payload;
      const { data, error } = await supabase
        .from("safety_rules")
        .update(patch)
        .eq("code", code)
        .select()
        .single();
      if (!error) return { data, error: null };
      lastError = error;

      const errCode = (error as { code?: string } | null)?.code;
      if (errCode !== "23514") {
        return { data: null, error };
      }
    }

    return { data: null, error: lastError };
  },

  async updateSeverity(code: string, severity: string) {
    return this.update(code, { severity });
  },

  async toggleActive(code: string, isActive: boolean) {
    return this.update(code, { is_active: isActive });
  },

  async archive(code: string) {
    const { error } = await supabase
      .from("safety_rules")
      .update({ is_active: false })
      .eq("code", code);
    return { error };
  },

  async delete(code: string) {
    const { error } = await supabase
      .from("safety_rules")
      .delete()
      .eq("code", code);
    return { error };
  },

  async seedDefaults() {
    const defaults = [
      { code: "SCHED_NO_CONTRACT", name: "Signed Contract Required", description: "Contract must be signed before scheduling", category: "Scheduling", severity: "Block", is_active: true, order_index: 1 },
      { code: "ORDER_NO_CONTRACT", name: "Contract for Ordering", description: "Cannot order without signed contract", category: "Material", severity: "Block", is_active: true, order_index: 10 },
      { code: "DISP_UNASSIGNED", name: "Crew Assignment", description: "Must have crew/subcontractor assigned", category: "Scheduling", severity: "Block", is_active: true, order_index: 20 },
      { code: "JOB_NOT_WON", name: "Quote Must Be Won", description: "Quote must be Won to create job", category: "Contract Terms", severity: "Block", is_active: true, order_index: 30 },
      { code: "PRICE_BELOW_FLOOR", name: "Below Floor Pricing", description: "Quote is priced below minimum floor", category: "Pricing", severity: "Block", is_active: true, order_index: 50 },
    ];
    const { data, error } = await supabase
      .from("safety_rules")
      .upsert(defaults, { onConflict: "code", ignoreDuplicates: true })
      .select();
    return { data, error };
  },
};

export interface OverrideLogRow {
  id?: string;
  rule_code: string;
  user_id: string;
  role?: string;
  quote_id?: string;
  job_id?: string;
  outcome: string;
  override_reason?: string;
  created_at?: string;
}

export const OverrideLogService = {
  async getAll(limit = 100) {
    const { data, error } = await supabase
      .from("override_log")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(limit);
    return { data, error };
  },

  async getByRuleCode(ruleCode: string, limit = 50) {
    const { data, error } = await supabase
      .from("override_log")
      .select("*")
      .eq("rule_code", ruleCode)
      .order("created_at", { ascending: false })
      .limit(limit);
    return { data, error };
  },

  async create(entry: Omit<OverrideLogRow, "id" | "created_at">) {
    const { data, error } = await supabase
      .from("override_log")
      .insert([entry])
      .select()
      .single();
    return { data, error };
  },
};
